import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import './Tests.css';

function Tests() {
  const navigate = useNavigate();
  const [tests, setTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [veraBalance, setVeraBalance] = useState(null);
  const [displayCount, setDisplayCount] = useState(5); // Show 5 tests initially

  useEffect(() => {
    loadTests();
    loadVeraBalance();

    // Setup Telegram WebApp Back Button
    if (window.Telegram?.WebApp) {
      const tg = window.Telegram.WebApp;
      tg.BackButton.show();
      tg.BackButton.onClick(() => navigate('/profile'));

      return () => {
        tg.BackButton.offClick(() => navigate('/profile'));
        tg.BackButton.hide();
      };
    }
  }, [navigate]);

  const loadVeraBalance = async () => {
    try {
      const response = await api.get('/vera/balance');
      setVeraBalance(response.data);
    } catch (error) {
      console.error('Failed to load VERA balance:', error);
    }
  };

  const loadTests = async () => {
    try {
      const response = await api.get('/feedback/my-tests', { params: { limit: 100 } });
      setTests(response.data.tests || []);
      setTotalCount(response.data.total || 0);
    } catch (error) {
      console.error('Failed to load tests:', error);
    } finally {
      setLoading(false);
    }
  };

  const getProviderName = (provider) => {
    const providers = {
      'mts': 'МТС',
      'megafon': 'Мегафон',
      'beeline': 'Билайн',
      'rostelecom': 'Ростелеком',
      'tele2': 'Tele2',
      'wifi': 'WiFi'
    };
    return providers[provider] || provider;
  };

  const getTimePeriodName = (period) => {
    const periods = {
      'night': 'Ночь',
      'day': 'День',
      'evening': 'Вечер'
    };
    return periods[period] || period;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      if (window.Telegram?.WebApp) {
        window.Telegram.WebApp.showAlert('URL скопирован в буфер обмена');
      }
    });
  };

  if (loading) {
    return (
      <div className="tests-page">
        <div className="loading">
          <div className="spinner"></div>
          <p>Загрузка тестов...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="tests-page">
      <div className="tests-header">
        <div className="points-display">
          <span className="points-icon">⭐</span>
          <span className="points-value">{veraBalance?.vera_points || 0}</span>
          <span className="points-label">VERA</span>
        </div>
        <div className="user-greeting">
          {window.Telegram?.WebApp?.initDataUnsafe?.user?.first_name || 'Гость'} 👋
        </div>
      </div>

      <div className="tests-title-section">
        <h2>🧪 Мои тесты</h2>
        <p className="tests-count">Всего пройдено: {totalCount}</p>
      </div>

      {tests.length === 0 ? (
        <div className="no-tests">
          <p>У вас пока нет пройденных тестов</p>
          <button className="btn-primary" onClick={() => navigate('/testing')}>
            Пройти тест
          </button>
        </div>
      ) : (
        <div className="tests-list">
          {tests.slice(0, displayCount).map((test) => (
            <div key={test.id} className={`test-card ${test.works ? 'success' : 'failed'}`}>
              <div className="test-card-header">
                <div className="test-status">
                  {test.works ? (
                    <span className="status-badge success">✅ Работает</span>
                  ) : (
                    <span className="status-badge failed">❌ Не работает</span>
                  )}
                </div>
                <div className="test-date">{formatDate(test.created_at)}</div>
              </div>

              <div className="test-card-body">
                <div className="test-info-grid">
                  <div className="test-info-item">
                    <span className="test-info-label">Провайдер:</span>
                    <span className="test-info-value">{getProviderName(test.provider)}</span>
                  </div>
                  <div className="test-info-item">
                    <span className="test-info-label">Время:</span>
                    <span className="test-info-value">{getTimePeriodName(test.time_period)}</span>
                  </div>
                  {test.server && (
                    <div className="test-info-item">
                      <span className="test-info-label">Сервер:</span>
                      <span className="test-info-value">
                        {test.server.country_flag} {test.server.country_name}
                      </span>
                    </div>
                  )}
                  {test.connection_speed && (
                    <div className="test-info-item">
                      <span className="test-info-label">Скорость:</span>
                      <span className="test-info-value speed">
                        {test.connection_speed === 'fast' && '🚀 Быстро'}
                        {test.connection_speed === 'medium' && '⚡ Средне'}
                        {test.connection_speed === 'slow' && '🐌 Медленно'}
                      </span>
                    </div>
                  )}
                </div>

                {test.subscription_url && (
                  <div className="test-url">
                    <div className="test-url-label">Конфигурация:</div>
                    <div className="test-url-value">
                      <span className="url-text-short">{test.subscription_url.substring(0, 50)}...</span>
                      <button
                        className="btn-copy-small"
                        onClick={() => copyToClipboard(test.subscription_url)}
                      >
                        📋
                      </button>
                    </div>
                  </div>
                )}

                {test.comment && (
                  <div className="test-comment">
                    <strong>Комментарий:</strong> {test.comment}
                  </div>
                )}

                {/* Retest button */}
                {test.subscription_url && (
                  <button
                    className="btn-retest"
                    onClick={async () => {
                      try {
                        const response = await api.post('/happ/generate-link', {
                          subscription_url: test.subscription_url,
                          provider: test.provider,
                          time_period: test.time_period,
                          source: 'retest'
                        });

                        if (response.data.connection_url) {
                          window.Telegram.WebApp.openLink(response.data.connection_url);
                        } else {
                          window.Telegram?.WebApp?.showAlert('Не удалось открыть в Happ');
                        }
                      } catch (error) {
                        console.error('Failed to open in Happ:', error);
                        window.Telegram?.WebApp?.showAlert('Ошибка при открытии конфигурации');
                      }
                    }}
                  >
                    🔄 Перетестировать в Happ
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Show More button */}
      {tests.length > displayCount && (
        <button
          className="btn-show-more"
          onClick={() => setDisplayCount(displayCount + 5)}
        >
          Показать еще ({tests.length - displayCount})
        </button>
      )}
    </div>
  );
}

export default Tests;
